import * as React from "react";
import { ThemeProvider as FluentThemeProvider } from "@fluentui/react";
import { themeFor } from "./themes";
import { metaThemeColor, ThemeName } from "./palette";

// A user's stored *preference* — never the resolved value. Storing "system"
// (not the current "dark") is what lets the app keep following the OS after
// the user changes it later.
export type ThemePreference = "light" | "dark" | "system";

export const THEME_STORAGE_KEY = "docubird-theme";
const SYSTEM_DARK_QUERY = "(prefers-color-scheme: dark)";

interface ThemeContextValue {
  /** What the user chose: light | dark | system. */
  preference: ThemePreference;
  /** What is actually rendered right now: light | dark. */
  effective: ThemeName;
  setPreference: (pref: ThemePreference) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(
  undefined
);

const prefersDark = (): boolean =>
  typeof window !== "undefined" &&
  !!window.matchMedia &&
  window.matchMedia(SYSTEM_DARK_QUERY).matches;

const readStoredPreference = (): ThemePreference => {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    /* private mode / storage blocked — fall through */
  }
  return "system";
};

const resolve = (pref: ThemePreference, systemDark: boolean): ThemeName =>
  pref === "system" ? (systemDark ? "dark" : "light") : pref;

/**
 * Applies the resolved theme to the document shell so hand-written CSS,
 * native controls, and browser chrome all match Fluent. Mirrors what the
 * pre-paint bootstrap script in index.html does, keeping them in lockstep.
 */
const applyToDocument = (effective: ThemeName): void => {
  const el = document.documentElement;
  el.setAttribute("data-theme", effective);
  el.style.colorScheme = effective; // native scrollbars / form controls
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", metaThemeColor[effective]);
};

export const ThemeProviderShell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [preference, setPreferenceState] = React.useState<ThemePreference>(
    readStoredPreference
  );
  const [systemDark, setSystemDark] = React.useState<boolean>(prefersDark);

  // Track the OS setting so "system" flips live (e.g. at dusk) without reload.
  React.useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(SYSTEM_DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    // addEventListener is the modern API; addListener is the Safari fallback.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const effective = resolve(preference, systemDark);

  React.useEffect(() => {
    applyToDocument(effective);
  }, [effective]);

  const setPreference = React.useCallback((pref: ThemePreference) => {
    setPreferenceState(pref);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, pref);
    } catch {
      /* ignore */
    }
  }, []);

  const value = React.useMemo<ThemeContextValue>(
    () => ({ preference, effective, setPreference }),
    [preference, effective, setPreference]
  );

  return (
    <ThemeContext.Provider value={value}>
      <FluentThemeProvider
        theme={themeFor(effective)}
        applyTo="element"
        style={{ height: "100%", width: "100%" }}
      >
        {children}
      </FluentThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemePreference = (): ThemeContextValue => {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) {
    throw new Error(
      "useThemePreference must be used within <ThemeProviderShell>"
    );
  }
  return ctx;
};
